import { redirect } from '@tanstack/react-router';

import { useAuthStore } from '@/lib/auth-store';
import { clearAllDrafts } from '@/lib/drafts';
import { queryClient } from '@/lib/query-client';

const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3100';

// Egyetlen futó refresh (dedup): párhuzamos 401-ek / route-váltások EGY refresh-hívást osztanak.
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * A megújítás HÁROM kimenete — és ez a különbségtétel a munkamenet élete:
 *   • `ok` — friss access-token a store-ban,
 *   • `unauthorized` — a refresh-cookie érvénytelen vagy visszavont (a BFF 401-et ad, és TÖRLI a cookie-t):
 *     a munkamenetnek tényleg vége, újra be kell lépni,
 *   • `transient` — átmeneti zavar (a BFF **503**-at ad Odin-hibára/fojtásra, a limiter 429-et, vagy a
 *     hálózat elesett): a refresh-cookie ÉRVÉNYES MARAD, tehát a munkamenetet NEM szabad eldobni.
 *
 * MIÉRT KELL (éles hiba): a burkoló minden bukást kiléptetésnek vett, holott a BFF
 * `POST /auth/refresh` ága SZÁNDÉKOSAN csak valódi 401-re dobja el a cookie-t (lásd `auth.controller.ts`).
 * Egy pillanatnyi Odin-fojtás így kiütötte a bejelentkezett felhasználót, aki kézzel lépett be újra — és
 * mivel az Odin a LOGINT fiókonként 5/15 perc-re korlátozza, pár kör után már a bejelentkezés is elakadt
 * („túl sok bejelentkezési kísérlet"). A szerver-oldali óvatosságot tehát a kliens tette semmissé.
 */
export type RefreshOutcome = 'ok' | 'unauthorized' | 'transient';

/**
 * A TABOK KÖZÖTTI SOROSÍTÁS NEVE — egy origin összes Atlas-fülére közös.
 *
 * MIÉRT KELL (mért, éles kockázat): a refresh-token ROTÁL, és az Odin REUSE-DETEKTÁLÁST végez — egy már
 * visszavont token újra-bemutatására az identity ÖSSZES tokenjét visszavonja (lopás-gyanú). A `refreshInFlight`
 * dedup csak EGY fülön belül véd; két fül viszont EGY cookie-tárolón osztozik, és ha mindkettő egyszerre
 * indít megújítást, a második a MÁR elhasznált értéket küldi → az Odin lopásnak látja → a felhasználó
 * MINDEN munkamenete megszűnik, mindkét fülön.
 *
 * MÉRVE ÉLESBEN: 737 rotációra 5 `auth.token_reuse` esemény (~0,7%) — ritka, de valódi, és pont azt a
 * „hirtelen kidobott" élményt adja, amit a felhasználó jelentett.
 *
 * A `navigator.locks` (Web Locks API) pont erre való: a lock birtokában a MÁSODIK fül megvárja az elsőt, és
 * utána a MÁR ROTÁLT cookie-val indul — tehát sosem mutat be elhasznált tokent. Ahol az API nem érhető el
 * (régi böngésző, nem-biztonságos kontextus), a hívás egyszerűen lock NÉLKÜL fut: a mai viselkedés marad.
 */
const REFRESH_LOCK = 'atlas-refresh';

/** A megújítás futtatása a tabok közötti lock alatt (ha a böngésző tudja). */
async function withCrossTabLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (locks === undefined) return run();
  return locks.request(REFRESH_LOCK, run);
}

/**
 * Csendes access-token megújítás a httpOnly refresh-cookie-ból (`POST /auth/refresh`, credentials: include).
 * A kimenet háromállapotú (`RefreshOutcome`) — a hívó ebből tudja, hogy kiléptetni kell-e.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  refreshInFlight ??= (async () => {
    try {
      const res = await withCrossTabLock(() =>
        fetch(`${baseUrl}/auth/refresh`, { method: 'POST', credentials: 'include' }),
      );
      // CSAK a 401 jelenti azt, hogy a munkamenetnek vége (a BFF ilyenkor törli is a cookie-t). Minden más
      // nem-ok válasz (503 · 429 · 5xx) ÁTMENETI: a cookie érvényes marad, a hívó ne léptessen ki.
      if (res.status === 401) return 'unauthorized';
      if (!res.ok) return 'transient';
      const body = (await res.json()) as { accessToken?: string };
      // 200, de nincs token: nem írunk üres tokent a store-ba. Ez szerződés-szegés, nem lejárt munkamenet —
      // ezért `transient` (a kiléptetés itt is fölösleges adatvesztés volna).
      if (!body.accessToken) return 'transient';
      useAuthStore.getState().setToken(body.accessToken);
      // A `/session` a TOKENBŐL él (jogkép + megszemélyesítés-állapot), a refresh viszont MÁS tokent ad:
      // a saját (aktor) tokenjét. Enélkül egy lejárt MEGSZEMÉLYESÍTŐ token után a felület továbbra is a
      // megszemélyesítés-sávot és a cél jogképét mutatná, holott már a saját nevünkben dolgozunk.
      void queryClient.invalidateQueries({ queryKey: ['session'] });
      return 'ok';
    } catch {
      // Hálózati hiba — a munkamenetről semmit nem tudunk, tehát NEM dobjuk el.
      return 'transient';
    } finally {
      // A dedup-slotot a következő mikrotaszkban engedjük el (a most várakozók még ezt az eredményt kapják).
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

/**
 * Visszafelé kompatibilis burkoló: `true` CSAK a sikeres megújításra. A hívók, amelyeknek elég a „sikerült-e"
 * (SSE-újrakapcsolás, profil-mentés utáni token-frissítés), ezt használják.
 */
export async function tryRefresh(): Promise<boolean> {
  return (await refreshSession()) === 'ok';
}

/**
 * Route-guard (beforeLoad): ha nincs memória-token (pl. reload után), előbb csendes refresh-t próbál;
 * ha a munkamenetnek tényleg vége → redirect a loginra. Így a bejelentkezett user reload után is bent marad.
 *
 * ÁTMENETI hibára (503/429/hálózat) EGYSZER, rövid várakozás után ÚJRAPRÓBÁL, és csak utána esik vissza a
 * login-redirectre. MIÉRT: a login az Odinban fiókonként 5/15 perc-re korlátozott — egy pillanatnyi zavarra
 * kiadott login-oldal arra csábít, hogy a felhasználó kézzel lépjen be újra, és pár kör után már a valódi
 * belépés is elakad. A cookie ilyenkor érvényes, tehát a második próbának jó esélye van.
 */
export async function requireAuth(): Promise<void> {
  if (useAuthStore.getState().token) return;
  let outcome = await refreshSession();
  if (outcome === 'transient') {
    await new Promise((resolve) => setTimeout(resolve, 1200));
    outcome = await refreshSession();
  }
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- a TanStack Router redirect-idiómája
  if (outcome !== 'ok') throw redirect({ to: '/login' });
}

/** Teljes kijelentkezés: refresh-token revoke az Odin-ban (a cookie-n át) + a memória-token törlése. */
export async function logoutEverywhere(): Promise<void> {
  await fetch(`${baseUrl}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(
    () => undefined,
  );
  useAuthStore.getState().logout();
  // a privát cache-elt adat (jegyzet, ünnepeltek, saját lista) nem maradhat a következő bejelentkezőnek
  queryClient.clear();
  // a félbehagyott vázlatok (jegy-válasz, üzenet) nem maradnak a gépen a munkamenet után
  clearAllDrafts();
}
